import { useContext } from "react";
import { useEditor } from "@craftjs/core";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import {
  ArrowClockwise as IconRedo,
  ArrowCounterclockwise as IconUndo,
  Eye as IconPreview,
  CloudArrowUp as IconPublish,
  Hdd as IconSave,
  Laptop as IconLaptop,
  Tablet as IconTablet,
  Phone as IconPhone,
  Display as IconDisplay,
} from "@modir/icons";
import lz from "lzutf8";
import { IBuilderContext, BuilderContext } from "../../providers";
import { StyledButton } from "../../components";

const HeaderDiv = styled.div`
  display: flex;
  align-items: center;
  background: var(--builder-toolbar-background);
  height: 42px;
  padding: 0 10px;
  box-shadow: var(--builder-toolbar-shadow);
`;

const LeftHeaderSection = styled.div`
  display: flex;
  flex-grow: 1;
`;

const MiddleHeaderSection = styled.div`
  position: absolute;
  left: 50%;
  margin-left: -70px;
`;

const RightHeaderSection = styled.div`
  display: flex;
`;

export const Header = () => {
  const { enabled, canUndo, canRedo, actions, query } = useEditor(
    (state, query) => ({
      enabled: state.options.enabled,
      canUndo: query.history.canUndo(),
      canRedo: query.history.canRedo(),
    })
  );

  const { onSave, onPreview, setViewport, viewport } =
    useContext<IBuilderContext>(BuilderContext);

  return (
    <HeaderDiv>
      {enabled && (
        <LeftHeaderSection>
          <StyledButton
            type="button"
            className="dark"
            disabled={!canUndo}
            onClick={() => actions.history.undo()}
          >
            <IconUndo /> Undo
          </StyledButton>
          <StyledButton
            type="button"
            className="dark"
            disabled={!canRedo}
            onClick={() => actions.history.redo()}
          >
            <IconRedo /> Redo
          </StyledButton>
        </LeftHeaderSection>
      )}
      <MiddleHeaderSection>
        <StyledButton
          type="button"
          className="dark"
          onClick={() => {
            setViewport?.({
              type: "desktop",
              width: 1200,
              height: 696,
            });
          }}
        >
          <IconDisplay
            css={css`
              margin: 0 !important;
              fill: ${viewport?.type == "desktop"
                ? `var(--builder-light-blue) !important`
                : null};
            `}
          />
        </StyledButton>
        <StyledButton
          type="button"
          className="dark"
          onClick={() => {
            setViewport?.({
              type: "laptop",
              width: 992,
              height: 696,
            });
          }}
        >
          <IconLaptop
            css={css`
              margin: 0 !important;
              fill: ${viewport?.type == "laptop"
                ? `var(--builder-light-blue) !important`
                : null};
            `}
          />
        </StyledButton>
        <StyledButton
          type="button"
          className="dark"
          onClick={() => {
            setViewport?.({
              type: "tablet",
              width: 576,
              height: 696,
            });
          }}
        >
          <IconTablet
            css={css`
              margin: 0 !important;
              fill: ${viewport?.type == "tablet"
                ? `var(--builder-light-blue) !important`
                : null};
            `}
          />
        </StyledButton>
        <StyledButton
          type="button"
          className="dark"
          onClick={() => {
            setViewport?.({
              type: "mobile",
              width: 360,
              height: 696,
            });
          }}
        >
          <IconPhone
            css={css`
              width: 14px !important;
              height: 14px !important;
              margin: 2px !important;
              fill: ${viewport?.type == "mobile"
                ? `var(--builder-light-blue) !important`
                : null};
            `}
          />
        </StyledButton>
      </MiddleHeaderSection>
      <RightHeaderSection>
        <StyledButton
          type="button"
          className="dark"
          onClick={() => {
            const json = query.serialize();
            const stateContent = lz.encodeBase64(lz.compress(json));
            onSave?.(stateContent);
          }}
        >
          <IconSave /> Save draft
        </StyledButton>
        <StyledButton
          type="button"
          className="dark"
          onClick={() => {
            const json = query.serialize();
            const stateContent = lz.encodeBase64(lz.compress(json));
            onPreview?.(stateContent);
          }}
        >
          <IconPreview /> Preview
        </StyledButton>
        <StyledButton type="button" className="dark">
          <IconPublish /> Publish
        </StyledButton>
      </RightHeaderSection>
    </HeaderDiv>
  );
};
